import {AbstractStore} from "./abstract-store";
import {action, autorun, computed, makeObservable, observable} from "mobx";
import {ApiLayerConfiguration} from "../config/auftragsverwaltung-api-configuration";
import {JsonSerializer, throwError} from "typescript-json-serializer";

import {Difference} from "../models/difference";
import {JournalStore} from "./journal-store-mobx";
import {ProjectProcess, ProjectProcessProps} from "../models/project-process";
import {MapViewerConfiguration} from "../config/map-viewer-configuration";
import {ProcessLogStore} from "./process-log-store-mobx";
import {ProjectType} from "./auftragsverwaltung-mobx";


export interface ProjectProcessStoreProps {
    layerConfig: ApiLayerConfiguration;
    mapViewerConfig: MapViewerConfiguration;
    journalStore: JournalStore;
    processLogStore: ProcessLogStore;
}

export class ProjectProcessStore extends AbstractStore {

    mapViewerConfig: MapViewerConfiguration;
    journalStore: JournalStore;
    processLogStore: ProcessLogStore;

    /**
     * Loaded items (local store)
     */
    items = observable.map<string, ProjectProcess>();

    constructor(props: ProjectProcessStoreProps) {

        super({ layerConfig: props.layerConfig });

        this.mapViewerConfig = props.mapViewerConfig;
        this.journalStore = props.journalStore;
        this.processLogStore = props.processLogStore;

        makeObservable(this, {
            status: observable,
            dataStatus: observable,

            items: observable,
            selectedItem: observable,
            setSelectedItem: action,
            resetSelectedItem: action,
            searchTerm: observable,
            visibleItems: computed,

            addItem: action,
            removeItem: action,
            createProcess: action,

            isDataLoading: computed,
            isDataLoaded: computed,
            isDataError: computed,
            isLoading: computed,
            isLoaded: computed,
            isNotFound: computed,
            isError: computed
        });
    }

    get visibleItems(): ProjectProcess[] {
        // apply filters
        const filtered = [...this.items.values()].filter(() => {
            return true;
        });
        return [ ...filtered];
    }

    createProcess(type: ProjectType, projectId: number | null, user: string): Promise<ProjectProcess | null> {
        return new Promise((resolve) => {
            if (!projectId) {
                resolve(null);
                return;
            }

            this.newItem(type, projectId, user).then((process: ProjectProcess) => {
                this.save(process, undefined, user).then(() => {
                    resolve(process);
                });
            });
        });
    }

    updateProcessStatus(process: ProjectProcess,
                        statusFrom: number,
                        statusTo: number,
                        user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!process) {
                    reject("Process undefined when updating.");
                    return;
                }

                const updated: any = process.updatedKeys;

                const feature = process?.feature;
                if (!feature) {
                    reject(`Feature undefined for process ${process.projectId}`);
                    return;
                }

                // TODO: use process log store
                // write process log
                this.processLogStore.logStatusChange(process.projectId, statusFrom, statusTo, user);

                // Loop through updated attributes and assign
                // the updated values to feature attributes.
                if (updated) {
                    Object.keys(updated).forEach((name) => {
                        feature.attributes[name] = updated[name];
                    });
                }

                this.saveFeature(feature, "update", user).then((objectId: number) => {
                    resolve(objectId);
                }, (error) => {
                    console.error("Error updating process", error);
                    reject();
                });
            });
        });
    }

    /**
     * Override addItem. Use projectId as key
     * @param item One item or a map with multiple items with GUID as key
     */
    addItem(item: ProjectProcess | Map<string, ProjectProcess>): void {
        if (item instanceof Map) {
            // multiple items
            this.items.merge(item);
        }
        else {
            // one item
            if (this.items.has(String(item.projectId))) {
                this.items.delete(String(item.projectId));
            }
            this.items.set(String(item.projectId), item);
        }
    }

    /**
     * Override removeItem. Use projectId as key
     * @param item
     */
    removeItem(item: ProjectProcess): void {
        if (this.items.has(String(item.projectId))) {
            this.items.delete(String(item.projectId));
        }
    }

    loadItem(projectId: number | null): Promise<ProjectProcess | null> {
        return new Promise((resolve) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!projectId) {
                    throw new Error("No project GUID specified.");
                }

                this.queryFeatures([projectId]).then((processes: ProjectProcess[]) => {
                    if (processes[0] && processes[0].projectId === projectId) {
                        const process = processes[0];
                        const localProcess = this.items.get(String(process.projectId));
                        if (localProcess) {
                            // return process as long as merging is in progress. process can be displayed with
                            // indicator that syncing is in process
                            // TODO: merge process with already loaded process
                        }

                        this.addItem(process);
                        this.setSelectedItem(process);
                        this.setDataStatus("loaded");
                        resolve(process);
                    } else {
                        // no process found for this project
                        resolve(null);
                    }
                }).catch((error) => {
                    console.error(error);
                    this.setDataStatus("error");
                    throw new Error("Error loading process");
                });
            });
        });

    }

    /**
     * Request processes from server and store in local store
     * @param projectIds (Optional) GUIDs of activities the processes are related to
     */
    loadItems(projectIds?: number[]): Promise<ProjectProcess[]> {
        return new Promise((resolve, reject) => {
            this.setDataStatus("loading");

            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                this.queryFeatures(projectIds).then((processes: ProjectProcess[]) => {
                    if (processes) {
                        // do only save to mobx when not requested with specified ids
                        const map = new Map<string, ProjectProcess>();
                        processes.forEach((p: ProjectProcess) => {
                            map.set(String(p.projectId), p);
                        });
                        this.addItem(map);
                        this.setDataStatus("loaded");

                        resolve(processes);
                    } else {
                        this.setDataStatus("error");
                        reject();
                        throw new Error("Error requesting process.");
                    }
                });
            });
        });
    }

    /**
     * Create a new item with a feature
     * Store item to local store and select
     * @param type
     * @param projectId
     * @param user
     */
    newItem(type: ProjectType, projectId: number | null, user: string): Promise<ProjectProcess | null> {
        return new Promise((resolve) => {
            if (!projectId) {
                resolve(null);
                return;
            }

            const processConfig = this.mapViewerConfig.getProcessConfig(type);
            if (!processConfig) {
                throw new Error(`No current process configuration found for process ${type}`);
            }
            const initialState = processConfig.states.find((status) => status.isStart);

            const process = new ProjectProcess({
                guid: crypto.randomUUID(),
                type: processConfig.type,
                version: processConfig.version,
                projectId,
                startDate: new Date(),
                statusId: initialState?.id,
                config: processConfig,
                creator: user,
                progress: 0
            });

            // create feature
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                // TODO: find out why the feature template prototype does not return attributes
                process.feature = this.createFeature(null, process);

                this.addItem(process);
                this.setSelectedItem(process);

                resolve(process);
            });
        });
    }

    /**
     * Save process to the server
     *
     * @param process
     * @param updated
     * @param user
     */
    save(process: ProjectProcess | null,
         updated: any,
         user: string): Promise<ProjectProcess | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!process?.guid) {
                    reject("Process GUID undefined when saving edits.");
                    return;
                }

                if (process.isNewRecord) {
                    // New project --> create feature
                    process.feature = this.createFeature(null, process);

                    // log project creation in journal
                    const nameDiff = new Difference({p: "title", n: process.title});
                    const idDiff = new Difference({p: "guid", n: process.guid});
                    this.journalStore?.journalizeItem(
                        process,
                        "project-process",
                        "add",
                        user,
                        [idDiff, nameDiff],
                        "Neuer Prozess"
                    );
                }

                if(!process.isNewRecord) {
                    // log changes to journal
                    const diff = this.compareAttributes(process, updated, ProjectProcess.comparableAttributes);
                    // do this before updating the attributes
                    this.journalStore?.journalizeItem(
                        process,
                        "project-process",
                        "update",
                        user,
                        diff,
                        "Prozess geändert"
                    );
                }

                const feature = process?.feature;
                if (!feature) {
                    reject(`Feature undefined for process ${process.guid}`);
                    return;
                }

                // Loop through updated attributes and assign
                // the updated values to feature attributes.
                if (updated) {
                    Object.keys(updated).forEach((name) => {
                        const dbFieldName = ProjectProcess.databaseFieldNames.get(name);
                        if (!dbFieldName) {
                            reject();
                            throw new Error(`Database field not found for process ${name}`);
                        }
                        feature.attributes[dbFieldName] = updated[name];
                    });
                }

                const mode = process.isNewRecord ? "add" : "update";
                this.saveFeature(feature, mode, user).then((objectId: number) => {
                    const attrs = updated || {};
                    attrs["objectId"] = objectId;
                    Object.assign(process, attrs);
                    if (process.feature) {
                        // update feature attributes also to allow deleting feature without page reload
                        process.feature.attributes["OBJECTID"] = objectId;
                    }

                    resolve(process);
                }, (error) => {
                    console.error("Error saving process", error);
                    reject(error);
                });
            });
        });
    }

    /**
     * Delete an process
     *
     * @param process
     * @param user
     */
    delete(process: ProjectProcess | null,
           user: string): Promise<number | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose(); // stop autorun as soon as api is available

                if (!process?.guid) {
                    reject("Process GUID undefined when saving edits.");
                    return;
                }

                if (process.isNewRecord) {
                    reject("This process is not saved. Does not have to be deleted.");
                    return;
                }

                const feature = process?.feature;
                if (!feature) {
                    reject(`Feature undefined for process ${process.guid}`);
                    return;
                }

                this.deleteFeature(feature).then((objectId: number) => {
                    this.resetSelectedItem();
                    this.removeItem(process);

                    // log deletion in journal
                    const nameDiff = new Difference({p: "title", o: process.title});
                    const idDiff = new Difference({p: "guid", o: process.guid});
                    this.journalStore?.journalizeItem(
                        process,
                        "project-process",
                        "delete",
                        user,
                        [idDiff, nameDiff],
                        "Prozess gelöscht"
                    );

                    resolve(objectId);
                });
            });
        });
    }

    queryFeatures(projectIds?: number[]): Promise<ProjectProcess[] | [] | null> {
        return new Promise((resolve, reject) => {
            autorun((reaction: any) => {
                if (!this.isLoaded) {
                    return;
                }
                reaction.dispose();

                // Get a query object for the layer's current configuration
                // queryParams.outFields will be set to ["*"] to get values
                // for all available fields.
                const queryParams = this.layer.createQuery();

                // load all features if no project ID is specified
                if (projectIds !== undefined) {
                    // Add to the layer's current definitionExpression
                    queryParams.where += ` AND PROJ_ID IN (${projectIds.join("','")})`;
                }

                // query the layer with the modified params object
                this.layer.queryFeatures(queryParams).then((results: __esri.FeatureSet) => {
                    if (results.features) {
                        const processes = this.parseQueryResult(results);
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        resolve(processes);
                    } else {
                        reject();
                    }
                }, (error) => {
                    throw new Error(`Error querying project processes + ${error}`);
                });
            });
        });
    }

    private parseQueryResult(data: __esri.FeatureSet): ProjectProcess[] {
        const serializer = new JsonSerializer({
            errorCallback: throwError
        });

        const processes: ProjectProcess[] = [];
        data.features?.forEach((feature: any) => {
            const json = serializer.deserialize(feature.attributes, ProjectProcess);
            const pp = new ProjectProcess(json as ProjectProcessProps);
            const processConfig = this.mapViewerConfig.getProcessConfig(pp.type, pp.version);
            if (!processConfig) {
                console.warn(`Process configuration not found for process ${pp.type} version ${pp.version}`);
                return;  // do not store processes where no configuration can be associated
            }
            pp.config = processConfig;
            pp.feature = feature;

            processes.push(pp);
        });
        return processes;
    }

}
